import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";

import Modal, { ModalControlProps } from "../../layouts/Modal";
import { Button } from "../base/Button";
import { TextField } from "../base/TextField";
import { useEbayAspects } from "../../data/TitleConfig/useTitleConfig";

interface AspectPickerModalProps extends ModalControlProps {
  partCategoryId: string;
  /** Aspect names already on the list, so they can be shown as added rather than offered again. */
  existingNames: string[];
  onConfirm: (names: string[]) => void;
}

/**
 * Search-and-multi-select over the eBay item aspects for the category's eBay category. Anything
 * eBay does not know about can still be typed straight into the list on the panel behind this,
 * so a missing or unmapped eBay category never blocks the screen.
 */
export default function AspectPickerModal({
  open,
  setOpen,
  partCategoryId,
  existingNames,
  onConfirm,
}: AspectPickerModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  const { aspects } = useEbayAspects(partCategoryId, open);

  useEffect(() => {
    if (open) return;

    setSearchTerm("");
    setSelected([]);
  }, [open]);

  const already = useMemo(
    () => new Set(existingNames.map((name) => name.toLowerCase())),
    [existingNames],
  );

  const term = searchTerm.trim().toLowerCase();

  const rows = useMemo(
    () =>
      (aspects.data?.aspects ?? []).filter(
        (aspect) =>
          !term ||
          aspect.name.toLowerCase().includes(term) ||
          aspect.values.some((value) => value.toLowerCase().includes(term)),
      ),
    [aspects.data, term],
  );

  const ebayCategoryId = aspects.data?.ebayCategoryId;
  const unmapped = !aspects.isLoading && !aspects.isError && !ebayCategoryId;

  const toggle = (name: string) =>
    setSelected((current) =>
      current.includes(name)
        ? current.filter((x) => x !== name)
        : [...current, name],
    );

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-lg">
      <h3 className="text-lg font-medium">Add eBay aspects</h3>
      <p className="mt-1 text-sm text-gray-600">
        The item specifics eBay lists for this category. Pick the ones worth
        putting in the title — each is added at Medium, and you set its priority
        and whether it is mandatory on the list afterwards.
      </p>

      {ebayCategoryId && (
        <p className="mt-2 text-xs text-gray-500">
          eBay category {ebayCategoryId}
        </p>
      )}

      <div className="mt-4">
        <TextField
          fullWidth
          small
          placeholder="Search aspects and values..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
        />
      </div>

      <ul className="mt-2 max-h-80 divide-y divide-gray-100 overflow-y-auto rounded border border-gray-200">
        {aspects.isLoading && (
          <li className="px-3 py-4 text-sm text-gray-500">
            Asking eBay for this category's aspects…
          </li>
        )}

        {/* Never claim eBay offers nothing when the request itself failed. */}
        {!aspects.isLoading && aspects.isError && (
          <li className="px-3 py-4">
            <p className="text-sm font-medium text-red-800">
              Could not load eBay aspects
            </p>
            <p className="mt-1 text-xs text-red-700">
              {(aspects.error as Error)?.message}
            </p>
          </li>
        )}

        {unmapped && (
          <li className="px-3 py-4">
            <p className="text-sm text-gray-600">
              This category has no eBay category mapped.
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Set the eBay Category ID in Title Type &amp; Basic Settings and
              save, then eBay can suggest aspects for it. Until then, type
              aspect names straight into the list.
            </p>
          </li>
        )}

        {!aspects.isLoading &&
          !aspects.isError &&
          !unmapped &&
          rows.length === 0 && (
            <li className="px-3 py-4 text-sm text-gray-500">
              {searchTerm
                ? "No aspect matches that search."
                : "eBay returned no aspects for this category."}
            </li>
          )}

        {rows.map((aspect) => {
          const isAdded = already.has(aspect.name.toLowerCase());

          return (
            <li key={aspect.name}>
              <label
                className={clsx(
                  "flex items-start gap-2 px-3 py-2 text-sm",
                  isAdded
                    ? "cursor-default bg-gray-50"
                    : "cursor-pointer hover:bg-gray-50",
                )}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 rounded border-gray-300 text-orange-600 focus:ring-orange-600 disabled:opacity-40"
                  disabled={isAdded}
                  checked={isAdded || selected.includes(aspect.name)}
                  onChange={() => toggle(aspect.name)}
                />

                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium">{aspect.name}</span>
                    {aspect.isRequired && (
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[11px] text-blue-700">
                        eBay required
                      </span>
                    )}
                    {isAdded && (
                      <span className="text-[11px] text-gray-500">
                        already added
                      </span>
                    )}
                  </span>

                  {aspect.values.length > 0 && (
                    <span className="mt-0.5 block truncate text-xs text-gray-500">
                      {aspect.values.slice(0, 6).join(", ")}
                      {aspect.values.length > 6 && "…"}
                    </span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 flex items-center justify-between gap-2">
        <span className="text-xs text-gray-500">
          {selected.length} selected
        </span>
        <div className="flex gap-2">
          <Button variant="outlined" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(selected)}
            disabled={selected.length === 0}
          >
            {`Add ${selected.length} aspect${selected.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
